import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

export default function CaseMessages({ caseId, scope = "claimant" }) {
  const t = useT();
  // scope: "claimant" -> /me/cases/{id}/messages, "admin" -> /admin/leads/{id}/messages
  const listUrl = scope === "admin" ? `/admin/leads/${caseId}/messages` : `/me/cases/${caseId}/messages`;
  const postUrl = scope === "admin" ? `/admin/leads/${caseId}/messages` : `/me/cases/${caseId}/messages`;
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(listUrl);
      setMessages(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!caseId) return;
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(postUrl, { body: text });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const isMine = (m) => (scope === "admin" ? m.sender_role === "admin" : m.sender_role !== "admin");

  return (
    <div className="flex flex-col" data-testid={`messages-${scope}`}>
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B68]">
        <MessageSquare className="h-3.5 w-3.5" />
        {scope === "admin" ? t("msg.thread.admin") : t("msg.thread.claimant")}
      </div>
      <div className="h-[320px] overflow-y-auto rounded-xl border border-[#E0E4E2] bg-white p-4" data-testid="messages-list">
        {loading && <div className="py-6 text-center text-sm text-[#5C6B68]">{t("msg.loading")}</div>}
        {!loading && messages.length === 0 && (
          <div className="flex h-full items-center justify-center text-center text-sm text-[#5C6B68]">
            {scope === "admin" ? t("msg.empty.admin") : t("msg.empty.claimant")}
          </div>
        )}
        <div className="flex flex-col gap-3">
          {messages.map((m) => {
            const mine = isMine(m);
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed ${mine ? "bg-[#E85D04] text-white" : "bg-[#FDFBF7] text-[#0B1317] border border-[#E0E4E2]"}`}>
                  <div className={`mb-0.5 text-[10px] font-bold uppercase tracking-wider ${mine ? "text-white/70" : "text-[#5C6B68]"}`}>
                    {mine ? t("msg.sender.you") : (m.sender_role === "admin" ? m.sender_name || t("msg.sender.team") : m.sender_name || t("msg.sender.claimant"))}
                  </div>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className={`mt-1 text-[10px] ${mine ? "text-white/60" : "text-[#5C6B68]"}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </div>

      <form onSubmit={send} className="mt-3 flex items-center gap-2" data-testid="message-form">
        <input
          className="input-field"
          placeholder={scope === "admin" ? t("msg.input.admin") : t("msg.input.claimant")}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={sending}
          data-testid="message-input"
        />
        <button type="submit" disabled={sending || !body.trim()} className="btn-primary !py-3 disabled:opacity-50" data-testid="message-send">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
